import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdNotificationsActive } from 'react-icons/md';
import {
  AppInput,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
} from '../../../components/ui';
import { sendAppUserNotification } from '../../../repositories/appUser.repository';

export function SendNotificationModal({ isOpen, onClose, user, onSent }) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [titleError, setTitleError] = useState('');
  const [bodyError, setBodyError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setBody('');
    setTitleError('');
    setBodyError('');
  }, [isOpen, user?.id]);

  if (!user) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    let ok = true;
    if (trimmedTitle.length < 3) {
      setTitleError('Enter a title (min 3 characters)');
      ok = false;
    }
    if (trimmedBody.length < 5) {
      setBodyError('Enter a message (min 5 characters)');
      ok = false;
    }
    if (!ok) return;

    setSaving(true);
    try {
      await sendAppUserNotification(user.id, {
        title: trimmedTitle,
        body: trimmedBody,
      });
      toast({
        title: 'Notification sent',
        description: `To ${user.fullName}`,
        status: 'success',
        position: 'top-right',
      });
      onSent?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to send notification',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pb={2}>
          Send notification
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={4}>
            <Box
              p={4}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="44px"
                  h="44px"
                  align="center"
                  justify="center"
                  borderRadius="sm"
                  bg="bg.amberTint"
                  flexShrink={0}
                >
                  <MdNotificationsActive size={22} color="#1A1A1A" />
                </Flex>
                <Box minW={0}>
                  <Text fontWeight="700" fontSize="15px" noOfLines={1}>
                    {user.fullName}
                  </Text>
                  <Text fontSize="13px" color="text.secondary" mt={1}>
                    {user.userTypeLabel} · appears in their Notifications tab
                  </Text>
                </Box>
              </Flex>
            </Box>

            <AppInput
              label="Title"
              isRequired
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleError('');
              }}
              placeholder="e.g. Document reminder"
              maxLength={200}
              isInvalid={!!titleError}
              errorMessage={titleError}
            />

            <AppTextarea
              label="Message"
              isRequired
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setBodyError('');
              }}
              placeholder="Write the notification the member will see…"
              rows={4}
              maxLength={1000}
              isInvalid={!!bodyError}
              errorMessage={bodyError}
              helperText={`${body.trim().length}/1000`}
            />
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Send
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
